import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiHelpCircle,
  FiXCircle,
} from 'react-icons/fi';
import { CheckCircle2, AlertTriangle } from 'lucide-react';

import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { useUpdatePregnancy } from '../../../helpers/api/pregnancy/pregnancy';
import { useCreatePriorDelivery } from '../../../helpers/api/patients/patients';
import {
  AnomalyDeliveryForm,
  NormalDeliveryForm,
} from '../features/AddPatientForm/tabs/AccouchementTab';
import { emptyPriorDelivery } from '../features/AddPatientForm/constants';
import type { PriorDeliveryRowState } from '../features/AddPatientForm/types';
import { useHtmlDataTheme } from '../../../services/useHtmlDataTheme';

type ClosingStatus = 'Delivered' | 'Miscarried' | 'Terminated' | 'Unknown';

const CLOSING_STATUSES: {
  value: ClosingStatus;
  titleKey: string;
  descriptionKey: string;
  Icon: React.ComponentType<{ size?: number; className?: string }>;
  activeLight: string;
  activeDark: string;
  iconActiveLight: string;
  iconActiveDark: string;
}[] = [
  {
    value: 'Delivered',
    titleKey: 'pregnancy_modal_pregnancy_status_delivered',
    descriptionKey: 'close_pregnancy_status_delivered_description',
    Icon: FiCheckCircle,
    activeLight:
      'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-300 text-emerald-900',
    activeDark:
      'border-emerald-500 bg-emerald-950 ring-2 ring-emerald-500 text-emerald-50',
    iconActiveLight: 'bg-emerald-100 text-emerald-700',
    iconActiveDark: 'bg-emerald-900 text-emerald-200',
  },
  {
    value: 'Terminated',
    titleKey: 'pregnancy_modal_pregnancy_status_terminated',
    descriptionKey: 'close_pregnancy_status_terminated_description',
    Icon: FiXCircle,
    activeLight:
      'border-amber-400 bg-amber-50 ring-2 ring-amber-300 text-amber-950',
    activeDark:
      'border-amber-500 bg-amber-950 ring-2 ring-amber-500 text-amber-50',
    iconActiveLight: 'bg-amber-100 text-amber-800',
    iconActiveDark: 'bg-amber-900 text-amber-100',
  },
  {
    value: 'Miscarried',
    titleKey: 'pregnancy_modal_pregnancy_status_miscarried',
    descriptionKey: 'close_pregnancy_status_miscarried_description',
    Icon: FiAlertTriangle,
    activeLight:
      'border-rose-400 bg-rose-50 ring-2 ring-rose-300 text-rose-900',
    activeDark:
      'border-rose-500 bg-rose-950 ring-2 ring-rose-500 text-rose-50',
    iconActiveLight: 'bg-rose-100 text-rose-700',
    iconActiveDark: 'bg-rose-900 text-rose-200',
  },
  {
    value: 'Unknown',
    titleKey: 'pregnancy_modal_pregnancy_status_unknown',
    descriptionKey: 'close_pregnancy_status_unknown_description',
    Icon: FiHelpCircle,
    activeLight:
      'border-slate-400 bg-slate-100 ring-2 ring-slate-300 text-slate-900',
    activeDark:
      'border-slate-500 bg-slate-800 ring-2 ring-slate-500 text-slate-100',
    iconActiveLight: 'bg-slate-200 text-slate-700',
    iconActiveDark: 'bg-slate-700 text-slate-100',
  },
];

const STATUS_FORCES_ANOMALY: Record<ClosingStatus, boolean> = {
  Delivered: false,
  Terminated: false,
  Miscarried: true,
  Unknown: false,
};

const STATUS_HAS_DELIVERY_FORM: Record<ClosingStatus, boolean> = {
  Delivered: true,
  Terminated: true,
  Miscarried: true,
  Unknown: false,
};

interface ClosePregnancyModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  pregnancyId: number;
  initialStatus?: ClosingStatus;
  onClosed?: (status: ClosingStatus) => void;
}

const buildPriorDeliveryPayload = (
  row: PriorDeliveryRowState,
  pregnancyId: number
): Record<string, unknown> => {
  const hasAnomaly = row.has_obstetric_anomalies && !!row.anomaly_type;
  const isNormal = !row.has_obstetric_anomalies;
  const isStillbirth = row.newborn_vital_status === 'STILLBIRTH';

  const anomalyDescription = (() => {
    if (!hasAnomaly) return null;
    const desc = row.anomaly_description.trim();
    if (
      row.anomaly_ga_days !== null &&
      row.anomaly_ga_days > 0 &&
      row.anomaly_ga_weeks !== null
    ) {
      const suffix = `[ga_days=${row.anomaly_ga_days}]`;
      return desc ? `${desc} ${suffix}` : suffix;
    }
    return desc || null;
  })();

  return {
    pregnancy_id: pregnancyId,
    delivery_date: row.delivery_date || null,
    delivery_type: isNormal ? row.delivery_type || null : null,
    delivery_ga_weeks: isNormal ? row.delivery_ga_weeks : null,
    delivery_ga_days: isNormal ? row.delivery_ga_days : null,
    pregnancy_term_status: isNormal ? row.pregnancy_term_status || null : null,
    extraction_modalities: row.extraction_modalities.trim() || null,
    cesarean_indication:
      isNormal && row.delivery_type === 'CESAREAN'
        ? row.cesarean_indication.trim() || null
        : null,
    delivery_place: isNormal ? row.delivery_place.trim() || null : null,
    delivery_complication_type: isNormal
      ? row.delivery_complication_type.trim() || null
      : null,
    newborn_complication_type:
      isNormal && !isStillbirth
        ? row.newborn_complication_type.trim() || null
        : null,
    newborn_weight_grams: isNormal ? row.newborn_weight_grams : null,
    newborn_sex: isNormal ? row.newborn_sex || null : null,
    newborn_vital_status: isNormal ? row.newborn_vital_status || null : null,
    has_obstetric_anomalies: row.has_obstetric_anomalies,
    anomaly_type: hasAnomaly ? row.anomaly_type || null : null,
    anomaly_date: hasAnomaly ? row.delivery_date || null : null,
    anomaly_place: hasAnomaly ? row.anomaly_place.trim() || null : null,
    anomaly_ga_weeks: hasAnomaly ? row.anomaly_ga_weeks : null,
    anomaly_description: anomalyDescription,
  };
};

const ClosePregnancyModal: React.FC<ClosePregnancyModalProps> = ({
  isOpen,
  onClose,
  patientId,
  pregnancyId,
  initialStatus,
  onClosed,
}) => {
  const [status, setStatus] = useState<ClosingStatus | ''>('');
  const [row, setRow] = useState<PriorDeliveryRowState>(() =>
    emptyPriorDelivery(false)
  );
  const [outcomeDate, setOutcomeDate] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate: updatePregnancy } = useUpdatePregnancy();
  const { mutate: createPriorDelivery } = useCreatePriorDelivery(
    undefined,
    undefined,
    false
  );

  const { t } = useTranslation('Patient');
  const { t: tP } = useTranslation('Patient');
  const { t: tPM } = useTranslation('PatientManagement');
  const isDarkTheme = useHtmlDataTheme() === 'dark';

  useEffect(() => {
    if (!isOpen) return;
    const next: ClosingStatus = initialStatus ?? ('' as ClosingStatus);
    setStatus(next || '');
    if (next) {
      const forced = STATUS_FORCES_ANOMALY[next];
      setRow(emptyPriorDelivery(forced));
    } else {
      setRow(emptyPriorDelivery(false));
    }
    setOutcomeDate('');
    setErrors({});
    setIsSubmitting(false);
  }, [isOpen, initialStatus]);

  const handleStatusChange = (next: ClosingStatus) => {
    setStatus(next);
    const forced = STATUS_FORCES_ANOMALY[next];
    setRow((prev) => ({
      ...emptyPriorDelivery(forced),
      delivery_date: prev.delivery_date,
    }));
    setOutcomeDate((prev) =>
      prev || row.delivery_date || dayjs().format('YYYY-MM-DD')
    );
    setErrors({});
  };

  const updateRow = <K extends keyof PriorDeliveryRowState>(
    _idx: number,
    key: K,
    value: PriorDeliveryRowState[K]
  ) => {
    setRow((prev) => ({ ...prev, [key]: value }));
  };

  const handleToggleAnomaly = (next: boolean) => {
    if (!status || status === 'Miscarried' || status === 'Unknown') return;
    setRow((prev) => ({ ...prev, has_obstetric_anomalies: next }));
    setErrors((prev) => {
      const cleared = { ...prev };
      delete cleared.anomaly_type;
      return cleared;
    });
  };

  const clearAnomalyError = () => {
    setErrors((prev) => {
      const cleared = { ...prev };
      delete cleared.anomaly_type;
      return cleared;
    });
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!status) {
      next._status = tPM('modals.closePregnancy.errors.pickStatus');
    } else if (STATUS_HAS_DELIVERY_FORM[status]) {
      if (row.has_obstetric_anomalies && !row.anomaly_type) {
        next.anomaly_type = tPM(
          'modals.closePregnancy.errors.anomalyTypeRequired'
        );
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    if (!status) return;

    setIsSubmitting(true);

    const clinicalOutcomeDate = STATUS_HAS_DELIVERY_FORM[status]
      ? row.delivery_date
      : outcomeDate;

    const updatePayload = {
      id: pregnancyId,
      patient_id: patientId,
      pregnancy_status: status,
      outcome_date: clinicalOutcomeDate || null,
    };

    updatePregnancy(updatePayload, {
      onSuccess: () => {
        if (!STATUS_HAS_DELIVERY_FORM[status]) {
          message.success(tPM('modals.closePregnancy.success.closed'));
          setIsSubmitting(false);
          onClosed?.(status);
          onClose();
          return;
        }
        const deliveryBody = buildPriorDeliveryPayload(row, pregnancyId);
        createPriorDelivery(
          { patient_id: patientId, ...deliveryBody },
          {
            onSuccess: () => {
              message.success(
                tPM('modals.closePregnancy.success.closedAndDelivery')
              );
              setIsSubmitting(false);
              onClosed?.(status);
              onClose();
            },
            onError: () => {
              setIsSubmitting(false);
              message.error(
                tPM('modals.closePregnancy.error.deliverySaveFailed')
              );
            },
          }
        );
      },
      onError: () => {
        setIsSubmitting(false);
        message.error(tPM('modals.closePregnancy.error.closeFailed'));
      },
    });
  };

  const selectedMeta = useMemo(
    () => CLOSING_STATUSES.find((s) => s.value === status) ?? null,
    [status]
  );

  if (!isOpen) return null;

  const showDeliveryForm = !!status && STATUS_HAS_DELIVERY_FORM[status];
  const lockAnomalyMode = status === 'Miscarried';
  const submitLabel = !status
    ? tPM('modals.closePregnancy.buttons.closeDefault')
    : status === 'Unknown'
      ? tPM('modals.closePregnancy.buttons.closeAsUnknown')
      : tPM('modals.closePregnancy.buttons.closeAndRecord');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <Modal.Header
        title={tPM('timeline.actions.closePregnancy')}
        onClose={onClose}
        description={
          <p className="text-sm text-title-page-color-300 dark:text-gray-400">
            {tP('close_pregnancy_description_before')}{' '}
            <span className="font-semibold">
              {tP('pregnancy_modal_pregnancy_status_delivered')}
            </span>
            ,{' '}
            <span className="font-semibold">
              {tP('pregnancy_modal_pregnancy_status_terminated')}
            </span>
            , {tP('close_pregnancy_description_or')}{' '}
            <span className="font-semibold">
              {tP('pregnancy_modal_pregnancy_status_miscarried')}
            </span>{' '}
            {tP('close_pregnancy_description_middle')}{' '}
            <span className="font-semibold">
              {tP('pregnancy_modal_pregnancy_status_unknown')}
            </span>{' '}
            {tP('close_pregnancy_description_after')}
          </p>
        }
      />

      <Modal.Body className="max-h-[min(70vh,640px)]">
        <div className="space-y-5">
          {/* --- Status picker --- */}
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-title-page-color-300 dark:text-gray-500">
              {tP('close_pregnancy_closing_status')}
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {CLOSING_STATUSES.map((opt) => {
                const isActive = status === opt.value;
                const Icon = opt.Icon;
                const activeCardClass = isDarkTheme
                  ? opt.activeDark
                  : opt.activeLight;
                const activeIconClass = isDarkTheme
                  ? opt.iconActiveDark
                  : opt.iconActiveLight;
                const inactiveCardClass = isDarkTheme
                  ? 'border-slate-700 bg-gray-800/80 text-gray-100 hover:border-slate-500'
                  : 'border-slate-200 bg-card-color-100 text-slate-900 hover:border-slate-300';
                const inactiveTitleClass = isDarkTheme
                  ? 'text-gray-100'
                  : 'text-title-page-color-100';
                const inactiveDescClass = isDarkTheme
                  ? 'text-gray-400'
                  : 'text-title-page-color-300';

                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleStatusChange(opt.value)}
                    className={
                      'flex items-start gap-3 rounded-xl border p-3 text-left transition-all ' +
                      (isActive
                        ? `${activeCardClass} shadow-sm`
                        : inactiveCardClass)
                    }
                  >
                    <span
                      className={
                        'mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ' +
                        (isActive
                          ? activeIconClass
                          : isDarkTheme
                            ? 'bg-gray-700 text-gray-300'
                            : 'bg-slate-100 text-slate-600')
                      }
                    >
                      <Icon size={16} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        className={
                          'block text-sm font-semibold ' +
                          (isActive ? 'text-inherit' : inactiveTitleClass)
                        }
                      >
                        {tP(opt.titleKey)}
                      </span>
                      <span
                        className={
                          'mt-0.5 block text-xs leading-relaxed ' +
                          (isActive
                            ? 'text-inherit opacity-90'
                            : inactiveDescClass)
                        }
                      >
                        {tP(opt.descriptionKey)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            {errors._status && (
              <p className="mt-2 text-xs font-medium text-red-600 dark:text-red-400">
                {errors._status}
              </p>
            )}
          </div>

          {/* --- Anomaly toggle (Delivered / Terminated only) --- */}
          {showDeliveryForm && !lockAnomalyMode && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-card-color-100 px-4 py-3 dark:border-slate-700 dark:bg-gray-800/50">
              <div>
                <p className="text-sm font-semibold text-title-page-color-100 dark:text-white">
                  {tP('close_pregnancy_outcome')}
                </p>
                <p className="mt-0.5 text-xs text-title-page-color-300 dark:text-gray-400">
                  {tP('close_pregnancy_outcome_question')}
                </p>
              </div>
              <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-xs font-medium dark:border-gray-700 dark:bg-gray-900/30">
                <button
                  type="button"
                  onClick={() => handleToggleAnomaly(false)}
                  className={
                    'inline-flex items-center gap-1 rounded-md px-3 py-1.5 transition-colors ' +
                    (!row.has_obstetric_anomalies
                      ? 'bg-white text-blue-700 shadow-sm dark:bg-gray-800 dark:text-blue-300'
                      : 'text-slate-600 hover:text-slate-900 dark:text-gray-400 dark:hover:text-gray-200')
                  }
                >
                  <CheckCircle2 size={13} />
                  {tP('close_pregnancy_no_anomaly')}
                </button>
                <button
                  type="button"
                  onClick={() => handleToggleAnomaly(true)}
                  className={
                    'inline-flex items-center gap-1 rounded-md px-3 py-1.5 transition-colors ' +
                    (row.has_obstetric_anomalies
                      ? 'bg-white text-amber-700 shadow-sm dark:bg-gray-800 dark:text-amber-300'
                      : 'text-slate-600 hover:text-slate-900 dark:text-gray-400 dark:hover:text-gray-200')
                  }
                >
                  <AlertTriangle size={13} />
                  {tP('close_pregnancy_with_anomaly')}
                </button>
              </div>
            </div>
          )}

          {/* --- Delivery form (forks on anomaly mode) --- */}
          {showDeliveryForm && (
            <div
              className={
                'rounded-xl border p-4 ' +
                (row.has_obstetric_anomalies
                  ? 'border-amber-200 bg-amber-50/40 dark:border-amber-800 dark:bg-amber-900/10'
                  : 'border-slate-200 bg-card-color-100 dark:border-slate-700 dark:bg-gray-800/40')
              }
            >
              {row.has_obstetric_anomalies ? (
                <AnomalyDeliveryForm
                  idx={0}
                  row={row}
                  errors={errors}
                  onUpdate={updateRow}
                  onClearAnomalyError={clearAnomalyError}
                  anomalyError={errors.anomaly_type}
                  t={t}
                />
              ) : (
                <NormalDeliveryForm
                  idx={0}
                  row={row}
                  errors={errors}
                  onUpdate={updateRow}
                  t={t}
                />
              )}
            </div>
          )}

          {/* --- Unknown status confirmation --- */}
          {status === 'Unknown' && (
            <div className="space-y-3 rounded-xl border border-slate-200 bg-card-color-100 p-4 dark:border-slate-700 dark:bg-gray-800/50">
              <div className="flex items-start gap-3">
                <FiHelpCircle
                  size={20}
                  className="mt-0.5 shrink-0 text-slate-500 dark:text-slate-400"
                />
                <p className="text-sm leading-relaxed text-title-page-color-300 dark:text-gray-400">
                  {tP('close_pregnancy_unknown_explain_before')}{' '}
                  <span className="font-semibold text-title-page-color-100 dark:text-white">
                    {tP('pregnancy_modal_pregnancy_status_unknown')}
                  </span>{' '}
                  {tP('close_pregnancy_unknown_explain_after')}
                </p>
              </div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-title-page-color-300 dark:text-gray-400">
                {tP('close_pregnancy_outcome_date')}
                <input
                  type="date"
                  value={outcomeDate}
                  onChange={(event) => setOutcomeDate(event.target.value)}
                  className="mt-1 block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-normal normal-case tracking-normal text-title-page-color-100 shadow-sm focus:border-primary-dui focus:outline-none focus:ring-2 focus:ring-primary-dui/20 dark:border-slate-700 dark:bg-gray-900 dark:text-white"
                />
              </label>
            </div>
          )}

          {selectedMeta && status !== 'Unknown' && (
            <p className="text-xs leading-relaxed text-title-page-color-300 dark:text-gray-400">
              {tP('close_pregnancy_summary_before')}{' '}
              <span className="font-semibold text-title-page-color-100 dark:text-white">
                {tP(selectedMeta.titleKey)}
              </span>{' '}
              {tP('close_pregnancy_summary_after')}{' '}
              <span className="font-mono text-[11px] tabular-nums">
                {dayjs(row.delivery_date || outcomeDate || undefined).format(
                  'MMM D, YYYY'
                )}
              </span>
              .
            </p>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
          {tPM('buttons.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !status}
        >
          {isSubmitting
            ? tPM('modals.closePregnancy.buttons.saving')
            : submitLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ClosePregnancyModal;
export type { ClosingStatus };
